import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Stack,
  Group,
  Button,
  Stepper,
  Text,
  Select,
  TextInput,
  Textarea,
  MultiSelect,
  Checkbox,
  ActionIcon,
  NumberInput,
  Accordion,
  ScrollArea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconArrowLeft,
  IconCheck,
  IconPlus,
  IconTrash,
  IconFilter,
  IconChartBar,
  IconSettings,
  IconCalendar,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useOrganization } from '@/hooks/useOrganization';
import { useCategories } from '@/hooks/api/useCategories';
import { useDepartments } from '@/hooks/api/useDepartments';
import { useLocations } from '@/hooks/api/useLocations';
import { useCreateCustomReport, useUpdateCustomReport } from '@/hooks/api/useCustomReports';
import type { ReportConfig, ReportField, ReportOrderBy, ReportGroupBy, ReportAggregation } from '@/hooks/api/useCustomReports';

const ENTITY_FIELDS: Record<string, Array<{
  value: string;
  label: string;
  format?: string;
  type?: 'numeric' | 'text' | 'date' | 'boolean'
}>> = {
  assets: [
    { value: 'tag_id', label: 'Asset ID', type: 'text' },
    { value: 'description', label: 'Description', type: 'text' },
    { value: 'status', label: 'Status', type: 'text' },
    { value: 'cost', label: 'Cost', format: 'currency', type: 'numeric' },
    { value: 'purchase_date', label: 'Purchase Date', format: 'date', type: 'date' },
    { value: 'category_id', label: 'Category', type: 'text' },
    { value: 'department_id', label: 'Department', type: 'text' },
    { value: 'location_id', label: 'Location', type: 'text' },
    { value: 'is_depreciable', label: 'Depreciable', type: 'boolean' },
    { value: 'created_at', label: 'Created At', format: 'datetime', type: 'date' },
  ],
};

const AGGREGATION_FUNCTIONS = [
  { value: 'count', label: 'Count' },
  { value: 'sum', label: 'Sum', numericOnly: true },
  { value: 'avg', label: 'Average', numericOnly: true },
  { value: 'min', label: 'Minimum', numericOnly: true },
  { value: 'max', label: 'Maximum', numericOnly: true },
] as const;

export function CreateReportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useOrganization();
  const { data: categories = [] } = useCategories(organizationId);
  const { data: departments = [] } = useDepartments(organizationId);
  const { data: locations = [] } = useLocations(organizationId);
  const createReport = useCreateCustomReport();
  const updateReport = useUpdateCustomReport();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // Get initial data from location state (from Assets page)
  const initialFilters = location.state?.filters;
  const editingReport = location.state?.editingReport;

  const form = useForm({
    initialValues: {
      name: editingReport?.name || '',
      description: editingReport?.description || '',
      entity: editingReport?.config.entities[0] || 'assets',
      fields: editingReport?.config.fields || [],
      filters: editingReport?.config.filters || initialFilters || {},
      orderBy: editingReport?.config.orderBy || [],
      groupBy: editingReport?.config.groupBy || [],
      aggregations: editingReport?.config.aggregations || [],
      is_shared: editingReport?.is_shared || false,
    },
  });

  // Pre-populate fields when editing or from initial filters
  useEffect(() => {
    if (editingReport) {
      setSelectedFields(editingReport.config.fields.map((f: any) => f.field));
    } else if (initialFilters) {
      form.setFieldValue('filters', initialFilters);
    }
  }, []);

  const availableFields = ENTITY_FIELDS[form.values.entity] || [];

  const addField = (fieldValue: string) => {
    if (!fieldValue || selectedFields.includes(fieldValue)) return;

    const field = availableFields.find(f => f.value === fieldValue);
    if (!field) return;

    const newField: ReportField = {
      entity: form.values.entity,
      field: field.value,
      label: field.label,
      format: field.format,
    };

    form.setFieldValue('fields', [...form.values.fields, newField]);
    setSelectedFields([...selectedFields, fieldValue]);
  };

  const removeField = (index: number) => {
    const newFields = form.values.fields.filter((_, i) => i !== index);
    form.setFieldValue('fields', newFields);
    setSelectedFields(newFields.map(f => f.field));
  };

  const addSorting = () => {
    if (form.values.fields.length === 0) return;
    const newSort: ReportOrderBy = {
      entity: form.values.entity,
      field: form.values.fields[0].field,
      direction: 'asc',
    };
    form.setFieldValue('orderBy', [...form.values.orderBy, newSort]);
  };

  const removeSorting = (index: number) => {
    const newSort = form.values.orderBy.filter((_, i) => i !== index);
    form.setFieldValue('orderBy', newSort);
  };

  const updateSorting = (index: number, updates: Partial<ReportOrderBy>) => {
    const newSort = [...form.values.orderBy];
    newSort[index] = { ...newSort[index], ...updates };
    form.setFieldValue('orderBy', newSort);
  };

  const addGrouping = () => {
    if (form.values.fields.length === 0) return;
    const newGroup: ReportGroupBy = {
      entity: form.values.entity,
      field: form.values.fields[0].field,
    };
    form.setFieldValue('groupBy', [...form.values.groupBy, newGroup]);
  };

  const removeGrouping = (index: number) => {
    const newGroup = form.values.groupBy.filter((_, i) => i !== index);
    form.setFieldValue('groupBy', newGroup);
  };

  const updateGrouping = (index: number, field: string) => {
    const newGroup = [...form.values.groupBy];
    newGroup[index] = { ...newGroup[index], field };
    form.setFieldValue('groupBy', newGroup);
  };

  const addAggregation = () => {
    const newAgg: ReportAggregation = {
      type: 'count',
      entity: form.values.entity,
      field: '*',
      alias: 'count_all',
    };
    form.setFieldValue('aggregations', [...form.values.aggregations, newAgg]);
  };

  const removeAggregation = (index: number) => {
    const newAgg = form.values.aggregations.filter((_, i) => i !== index);
    form.setFieldValue('aggregations', newAgg);
  };

  const updateAggregation = (index: number, updates: Partial<ReportAggregation>) => {
    const newAgg = [...form.values.aggregations];
    newAgg[index] = { ...newAgg[index], ...updates };

    if (updates.type || updates.field) {
      const agg = newAgg[index];
      newAgg[index].alias = `${agg.type}_${agg.field === '*' ? 'all' : agg.field}`;
    }

    form.setFieldValue('aggregations', newAgg);
  };

  const handleSubmit = async () => {
    const values = form.values;

    if (!values.name.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please enter a report name',
        color: 'red',
      });
      setActiveStep(0);
      return;
    }

    if (values.fields.length === 0) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select at least one field',
        color: 'red',
      });
      setActiveStep(1);
      return;
    }

    const config: ReportConfig = {
      entities: [values.entity],
      fields: values.fields,
      filters: Object.keys(values.filters).length > 0 ? values.filters : undefined,
      groupBy: values.groupBy.length > 0 ? values.groupBy : undefined,
      aggregations: values.aggregations.length > 0 ? values.aggregations : undefined,
      orderBy: values.orderBy.length > 0 ? values.orderBy : undefined,
    };

    try {
      if (editingReport) {
        await updateReport.mutateAsync({
          id: editingReport.id,
          input: {
            name: values.name,
            description: values.description,
            config,
            is_shared: values.is_shared,
          },
        });
        notifications.show({
          title: 'Report Updated',
          message: 'Your report has been updated successfully',
          color: 'green',
        });
      } else {
        await createReport.mutateAsync({
          organization_id: organizationId,
          name: values.name,
          description: values.description,
          config,
          is_shared: values.is_shared,
        });
        notifications.show({
          title: 'Report Created',
          message: 'Your report has been created successfully',
          color: 'green',
        });
      }
      navigate('/reports/saved');
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save report',
        color: 'red',
      });
    }
  };

  const nextStep = () => {
    if (activeStep === 0 && !form.values.name.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please enter a report name',
        color: 'yellow',
      });
      return;
    }
    if (activeStep === 1 && form.values.fields.length === 0) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select at least one field',
        color: 'yellow',
      });
      return;
    }
    setActiveStep((current) => (current < 4 ? current + 1 : current));
  };

  const prevStep = () => setActiveStep((current) => (current > 0 ? current - 1 : current));

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" onClick={() => navigate('/reports/saved')}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={1}>{editingReport ? 'Edit Report' : 'Create New Report'}</Title>
              <Text size="sm" c="dimmed" mt={4}>
                {editingReport ? 'Modify your existing report' : 'Build a custom report with filters and grouping'}
              </Text>
            </div>
          </Group>
        </Group>

        {/* Stepper */}
        <Paper p="lg" withBorder>
          <Stepper active={activeStep} onStepClick={setActiveStep}>
            <Stepper.Step label="Basic Info" description="Name and description" icon={<IconSettings size={18} />}>
              <Stack gap="md" mt="xl">
                <TextInput
                  label="Report Name"
                  placeholder="Enter report name"
                  required
                  {...form.getInputProps('name')}
                />
                <Textarea
                  label="Description"
                  placeholder="Describe what this report shows"
                  rows={3}
                  {...form.getInputProps('description')}
                />
                <Select
                  label="Entity"
                  data={[{ value: 'assets', label: 'Assets' }]}
                  {...form.getInputProps('entity')}
                  disabled
                />
                <Checkbox
                  label="Share this report with other users in my organization"
                  {...form.getInputProps('is_shared', { type: 'checkbox' })}
                />
              </Stack>
            </Stepper.Step>

            <Stepper.Step label="Fields" description="Select columns" icon={<IconCheck size={18} />}>
              <Stack gap="md" mt="xl">
                <div>
                  <Text fw={500} size="sm" mb="xs">Select Fields to Include</Text>
                  <Select
                    placeholder="Choose a field to add"
                    data={availableFields
                      .filter(f => !selectedFields.includes(f.value))
                      .map(f => ({ value: f.value, label: f.label }))}
                    onChange={(value) => value && addField(value)}
                    clearable
                  />
                </div>

                {form.values.fields.length > 0 && (
                  <Paper p="md" withBorder>
                    <Text fw={500} size="sm" mb="sm">Selected Fields ({form.values.fields.length})</Text>
                    <Stack gap="xs">
                      {form.values.fields.map((field, index) => (
                        <Group key={index} justify="space-between">
                          <Text size="sm">{field.label}</Text>
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => removeField(index)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Stepper.Step>

            <Stepper.Step label="Filters" description="Filter data" icon={<IconFilter size={18} />}>
              <ScrollArea h={400} mt="xl">
                <Accordion variant="contained">
                  <Accordion.Item value="filters">
                    <Accordion.Control icon={<IconFilter size={20} />}>
                      <Title order={5}>Asset Filters</Title>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="md">
                        <MultiSelect
                          label="Status"
                          placeholder="Select statuses"
                          data={[
                            { value: 'Available', label: 'Available' },
                            { value: 'In Use', label: 'In Use' },
                            { value: 'Maintenance', label: 'Maintenance' },
                            { value: 'Retired', label: 'Retired' },
                            { value: 'Lost', label: 'Lost' },
                          ]}
                          value={form.values.filters.assets?.status || []}
                          onChange={(value) =>
                            form.setFieldValue('filters', {
                              ...form.values.filters,
                              assets: { ...form.values.filters.assets, status: value },
                            })
                          }
                        />

                        <MultiSelect
                          label="Categories"
                          placeholder="Select categories"
                          data={categories.map(c => ({ value: c.id, label: c.name }))}
                          value={form.values.filters.assets?.category_id || []}
                          onChange={(value) =>
                            form.setFieldValue('filters', {
                              ...form.values.filters,
                              assets: { ...form.values.filters.assets, category_id: value },
                            })
                          }
                          searchable
                        />

                        <MultiSelect
                          label="Departments"
                          placeholder="Select departments"
                          data={departments.map(d => ({ value: d.id, label: d.name }))}
                          value={form.values.filters.assets?.department_id || []}
                          onChange={(value) =>
                            form.setFieldValue('filters', {
                              ...form.values.filters,
                              assets: { ...form.values.filters.assets, department_id: value },
                            })
                          }
                          searchable
                        />

                        <MultiSelect
                          label="Locations"
                          placeholder="Select locations"
                          data={locations.map(l => ({ value: l.id, label: l.name }))}
                          value={form.values.filters.assets?.location_id || []}
                          onChange={(value) =>
                            form.setFieldValue('filters', {
                              ...form.values.filters,
                              assets: { ...form.values.filters.assets, location_id: value },
                            })
                          }
                          searchable
                        />

                        <Group grow>
                          <NumberInput
                            label="Min Cost"
                            placeholder="Minimum"
                            min={0}
                            prefix="$"
                            value={form.values.filters.assets?.cost_min}
                            onChange={(value) =>
                              form.setFieldValue('filters', {
                                ...form.values.filters,
                                assets: { ...form.values.filters.assets, cost_min: value },
                              })
                            }
                          />
                          <NumberInput
                            label="Max Cost"
                            placeholder="Maximum"
                            min={0}
                            prefix="$"
                            value={form.values.filters.assets?.cost_max}
                            onChange={(value) =>
                              form.setFieldValue('filters', {
                                ...form.values.filters,
                                assets: { ...form.values.filters.assets, cost_max: value },
                              })
                            }
                          />
                        </Group>

                        <Checkbox
                          label="Depreciable assets only"
                          checked={form.values.filters.assets?.is_depreciable || false}
                          onChange={(e) =>
                            form.setFieldValue('filters', {
                              ...form.values.filters,
                              assets: {
                                ...form.values.filters.assets,
                                is_depreciable: e.currentTarget.checked || undefined,
                              },
                            })
                          }
                        />
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </ScrollArea>
            </Stepper.Step>

            <Stepper.Step label="Grouping" description="Group and aggregate" icon={<IconChartBar size={18} />}>
              <Stack gap="md" mt="xl">
                {/* Grouping */}
                <Paper p="md" withBorder>
                  <Group justify="apart" mb="sm">
                    <div>
                      <Text fw={500} size="sm">Group By</Text>
                      <Text size="xs" c="dimmed">Group rows by field values</Text>
                    </div>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size={14} />}
                      onClick={addGrouping}
                      disabled={form.values.fields.length === 0}
                    >
                      Add Group
                    </Button>
                  </Group>

                  {form.values.groupBy.length === 0 ? (
                    <Text size="sm" c="dimmed">No grouping configured</Text>
                  ) : (
                    <Stack gap="xs">
                      {form.values.groupBy.map((group, index) => (
                        <Group key={index} gap="xs">
                          <Select
                            placeholder="Field"
                            data={availableFields.map(f => ({ value: f.value, label: f.label }))}
                            value={group.field}
                            onChange={(value) => value && updateGrouping(index, value)}
                            style={{ flex: 1 }}
                          />
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => removeGrouping(index)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </Paper>

                {/* Aggregations */}
                {form.values.groupBy.length > 0 && (
                  <Paper p="md" withBorder>
                    <Group justify="apart" mb="sm">
                      <div>
                        <Text fw={500} size="sm">Aggregations</Text>
                        <Text size="xs" c="dimmed">Calculate metrics for each group</Text>
                      </div>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconPlus size={14} />}
                        onClick={addAggregation}
                      >
                        Add Aggregation
                      </Button>
                    </Group>

                    {form.values.aggregations.length === 0 ? (
                      <Text size="sm" c="dimmed">No aggregations defined</Text>
                    ) : (
                      <Stack gap="xs">
                        {form.values.aggregations.map((agg, index) => {
                          const numericFields = availableFields.filter(f => f.type === 'numeric');
                          const availableFunctions = AGGREGATION_FUNCTIONS.filter(
                            fn => !fn.numericOnly || agg.field === '*' || numericFields.some(f => f.value === agg.field)
                          );

                          return (
                            <Group key={index} gap="xs">
                              <Select
                                placeholder="Function"
                                data={availableFunctions.map(f => ({ value: f.value, label: f.label }))}
                                value={agg.type}
                                onChange={(value) => updateAggregation(index, { type: value as any })}
                                style={{ width: 120 }}
                              />
                              <Select
                                placeholder="Field"
                                data={[
                                  { value: '*', label: 'All Records' },
                                  ...numericFields.map(f => ({ value: f.value, label: f.label })),
                                ]}
                                value={agg.field}
                                onChange={(value) => updateAggregation(index, { field: value || '*' })}
                                style={{ flex: 1 }}
                                disabled={agg.type === 'count'}
                              />
                              <ActionIcon
                                color="red"
                                variant="subtle"
                                onClick={() => removeAggregation(index)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Group>
                          );
                        })}
                      </Stack>
                    )}
                  </Paper>
                )}

                {/* Sorting */}
                <Paper p="md" withBorder>
                  <Group justify="apart" mb="sm">
                    <div>
                      <Text fw={500} size="sm">Sorting</Text>
                      <Text size="xs" c="dimmed">Order results</Text>
                    </div>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size={14} />}
                      onClick={addSorting}
                      disabled={form.values.fields.length === 0}
                    >
                      Add Sort
                    </Button>
                  </Group>

                  {form.values.orderBy.length === 0 ? (
                    <Text size="sm" c="dimmed">No sorting configured</Text>
                  ) : (
                    <Stack gap="xs">
                      {form.values.orderBy.map((sort, index) => (
                        <Group key={index} gap="xs">
                          <Select
                            placeholder="Field"
                            data={availableFields.map(f => ({ value: f.value, label: f.label }))}
                            value={sort.field}
                            onChange={(value) => value && updateSorting(index, { field: value })}
                            style={{ flex: 1 }}
                          />
                          <Select
                            placeholder="Direction"
                            data={[
                              { value: 'asc', label: 'Ascending' },
                              { value: 'desc', label: 'Descending' },
                            ]}
                            value={sort.direction}
                            onChange={(value) =>
                              updateSorting(index, { direction: value as 'asc' | 'desc' })
                            }
                            style={{ width: 140 }}
                          />
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => removeSorting(index)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Stack>
            </Stepper.Step>

            <Stepper.Step label="Automation" description="Schedule & delivery" icon={<IconCalendar size={18} />}>
              <Stack gap="md" mt="xl" align="center" py="xl">
                <IconCalendar size={64} stroke={1.5} style={{ color: 'var(--mantine-color-dimmed)' }} />
                <div style={{ textAlign: 'center' }}>
                  <Title order={3}>Report Automation</Title>
                  {editingReport ? (
                    <>
                      <Text size="sm" c="dimmed" mt="xs">
                        Schedule this report to run automatically and deliver results via email.
                      </Text>
                      <Button
                        mt="md"
                        leftSection={<IconCalendar size={16} />}
                        onClick={() => navigate(`/reports/automations?reportId=${editingReport.id}`)}
                      >
                        Create Schedule for This Report
                      </Button>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed" mt="xs">
                      Save this report first, then you can create a schedule to run it automatically.
                    </Text>
                  )}
                </div>
              </Stack>
            </Stepper.Step>

            <Stepper.Completed>
              <Stack gap="md" mt="xl" align="center" py="xl">
                <IconCheck size={64} stroke={1.5} style={{ color: 'var(--mantine-color-green)' }} />
                <div style={{ textAlign: 'center' }}>
                  <Title order={3}>Ready to Save</Title>
                  <Text size="sm" c="dimmed" mt="xs">
                    Your report is configured and ready to be saved.
                  </Text>
                </div>
              </Stack>
            </Stepper.Completed>
          </Stepper>

          {/* Navigation Buttons */}
          <Group justify="space-between" mt="xl">
            <Button variant="default" onClick={prevStep} disabled={activeStep === 0}>
              Back
            </Button>
            <Group>
              <Button variant="subtle" onClick={() => navigate('/reports/saved')}>
                Cancel
              </Button>
              {activeStep < 4 ? (
                <Button onClick={nextStep}>Next Step</Button>
              ) : (
                <Button onClick={handleSubmit} loading={createReport.isPending || updateReport.isPending}>
                  {editingReport ? 'Update Report' : 'Create Report'}
                </Button>
              )}
            </Group>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}
